import { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Send, Image as ImageIcon, Pin, Flag, Trash2, MessageCircle, X, Bell } from "lucide-react";
import api from "../lib/api";
import { isNoldDomain } from "../lib/domain";

export default function Forum() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [channels, setChannels] = useState([]);
  const [currentKey, setCurrentKey] = useState(null);
  const [messages, setMessages] = useState([]);
  const [pinned, setPinned] = useState([]);
  const [channelInfo, setChannelInfo] = useState(null);
  const [text, setText] = useState("");
  const [imgUrl, setImgUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const scrollerRef = useRef(null);
  const brandName = isNoldDomain() ? "Nold Go" : "BentSkin Go";

  const loadMe = useCallback(async () => {
    try { const r = await api.get("/auth/me"); setMe(r.data); } catch { nav("/auth"); }
  }, [nav]);

  const loadChannels = useCallback(async () => {
    try { const r = await api.get("/forum/channels"); setChannels(r.data.channels || []); }
    catch (e) { /* silent */ }
  }, []);

  const loadMessages = useCallback(async (key) => {
    if (!key) return;
    try {
      const r = await api.get(`/forum/channels/${key}/messages`);
      setMessages(r.data.messages || []);
      setPinned(r.data.pinned || []);
      setChannelInfo(r.data.channel || null);
      // Marquer lu
      api.post(`/forum/channels/${key}/read`).catch(() => {});
      setTimeout(() => { scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" }); }, 100);
    } catch (e) {
      toast.error("Impossible de charger le canal");
    }
  }, []);

  useEffect(() => { loadMe(); loadChannels(); }, [loadMe, loadChannels]);
  useEffect(() => {
    if (!currentKey) return;
    loadMessages(currentKey);
    const iv = setInterval(() => loadMessages(currentKey), 20000);
    return () => clearInterval(iv);
  }, [currentKey, loadMessages]);
  // Recharge canaux (badges non-lus) toutes les 30s
  useEffect(() => { const iv = setInterval(loadChannels, 30000); return () => clearInterval(iv); }, [loadChannels]);

  const send = async () => {
    if (sending) return;
    const body = text.trim();
    if (!body && !imgUrl) return;
    setSending(true);
    try {
      await api.post(`/forum/channels/${currentKey}/messages`, { text: body, image_url: imgUrl || null });
      setText(""); setImgUrl("");
      await loadMessages(currentKey);
      await loadChannels();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible");
    } finally { setSending(false); }
  };

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error("Image trop lourde (max 8 Mo)"); return; }
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.post("/forum/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImgUrl(r.data.url);
      toast.success("Photo prête à être envoyée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload échoué");
    } finally { setUploading(false); }
  };

  const pin = async (id) => {
    try { const r = await api.post(`/forum/messages/${id}/pin`); toast.success(r.data.pinned ? "Message épinglé" : "Message désépinglé"); await loadMessages(currentKey); }
    catch (e) { toast.error("Action non autorisée"); }
  };
  const del = async (id) => {
    if (!window.confirm("Supprimer ce message ?")) return;
    try { await api.delete(`/forum/messages/${id}`); toast.success("Message supprimé"); await loadMessages(currentKey); }
    catch (e) { toast.error(e?.response?.data?.detail || "Suppression non autorisée"); }
  };
  const report = async (id) => {
    const reason = window.prompt("Raison du signalement (facultatif)") || "";
    try { const r = await api.post(`/forum/messages/${id}/report`, { reason }); toast.success(r.data.already ? "Déjà signalé" : "Signalement envoyé, merci"); }
    catch (e) { toast.error("Signalement impossible"); }
  };

  const isAdmin = me?.role === "admin";
  const canPost = channelInfo && (!channelInfo.admin_only_post || isAdmin);

  if (!currentKey) {
    return (
      <div className="min-h-screen bg-[#F4F0EA]" data-testid="forum-home">
        <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E2DFD8]">
          <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
            <button data-testid="forum-back" onClick={() => nav(-1)} className="p-2 rounded-lg hover:bg-[#F4F0EA]"><ArrowLeft className="w-5 h-5" /></button>
            <div>
              <div className="font-head font-black text-[#1A1A1A] leading-tight">Forum {brandName}</div>
              <div className="text-xs text-[#75807A]">Communauté & entraide</div>
            </div>
          </div>
        </header>
        <main className="max-w-3xl mx-auto px-4 py-6">
          <div className="grid gap-3">
            {channels.map((c) => (
              <button key={c.id} data-testid={`forum-channel-${c.key}`} onClick={() => setCurrentKey(c.key)} className="text-left bg-white rounded-2xl border border-[#E2DFD8] p-4 hover:border-[#F26419] transition-colors flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#F4F0EA] flex items-center justify-center text-2xl shrink-0">{c.icon}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-head font-bold text-[#1A1A1A] flex items-center gap-2">
                    {c.name}
                    {c.admin_only_post && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[#F26419]/10 text-[#D94F2B]">Direction</span>}
                  </div>
                  <div className="text-xs text-[#75807A] truncate">{c.desc}</div>
                </div>
                {c.unread > 0 && (
                  <span data-testid={`forum-unread-${c.key}`} className="ml-2 min-w-[24px] h-6 px-2 rounded-full bg-[#F26419] text-white text-xs font-black flex items-center justify-center">{c.unread > 99 ? "99+" : c.unread}</span>
                )}
              </button>
            ))}
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F0EA] flex flex-col" data-testid="forum-channel-view">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E2DFD8]">
        <div className="max-w-3xl mx-auto px-4 h-14 flex items-center gap-3">
          <button data-testid="forum-back-to-list" onClick={() => setCurrentKey(null)} className="p-2 rounded-lg hover:bg-[#F4F0EA]"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex-1 min-w-0">
            <div className="font-head font-black text-[#1A1A1A] leading-tight truncate">{channelInfo?.name || "Canal"}</div>
            <div className="text-xs text-[#75807A]">Forum {brandName}</div>
          </div>
        </div>
      </header>

      {pinned.length > 0 && (
        <div className="max-w-3xl mx-auto w-full px-4 pt-3">
          <div className="bg-[#FFF7ED] border border-[#F26419]/30 rounded-2xl p-3" data-testid="forum-pinned">
            <div className="text-[11px] font-bold uppercase tracking-wide text-[#D94F2B] flex items-center gap-1.5 mb-1"><Pin className="w-3 h-3" /> Épinglé</div>
            {pinned.map((m) => (
              <div key={m.id} className="text-sm text-[#1A1A1A]"><span className="font-semibold">{m.author_name}</span> — {m.text}</div>
            ))}
          </div>
        </div>
      )}

      <main ref={scrollerRef} className="flex-1 overflow-y-auto max-w-3xl mx-auto w-full px-4 py-4">
        <div className="space-y-3">
          {messages.length === 0 && (
            <div className="text-center text-[#75807A] text-sm py-16">
              <MessageCircle className="w-10 h-10 mx-auto mb-2 opacity-40" />
              Aucun message pour l'instant. Soyez le premier !
            </div>
          )}
          {messages.map((m) => (
            <MessageBubble key={m.id} m={m} me={me} isAdmin={isAdmin} onPin={pin} onDel={del} onReport={report} />
          ))}
        </div>
      </main>

      {canPost ? (
        <footer className="sticky bottom-0 bg-white border-t border-[#E2DFD8]">
          <div className="max-w-3xl mx-auto px-3 py-3">
            {imgUrl && (
              <div className="mb-2 relative inline-block">
                <img src={imgUrl} alt="À envoyer" className="max-h-24 rounded-xl border border-[#E2DFD8]" />
                <button data-testid="forum-clear-image" onClick={() => setImgUrl("")} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white shadow border border-[#E2DFD8] flex items-center justify-center"><X className="w-3 h-3" /></button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <label className="w-11 h-11 shrink-0 rounded-xl bg-[#F4F0EA] flex items-center justify-center cursor-pointer hover:bg-[#EFEBE4]" data-testid="forum-upload-label">
                <input data-testid="forum-file-input" type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
                {uploading ? <span className="text-xs">…</span> : <ImageIcon className="w-5 h-5 text-[#3B423E]" />}
              </label>
              <textarea data-testid="forum-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={channelInfo?.admin_only_post ? "Annonce direction…" : "Votre message…"} rows={1} className="flex-1 resize-none rounded-xl border border-[#E2DFD8] px-3 py-2.5 text-sm focus:outline-none focus:border-[#F26419] max-h-32" onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} />
              <button data-testid="forum-send" onClick={send} disabled={sending || (!text.trim() && !imgUrl)} className="w-11 h-11 shrink-0 rounded-xl bg-[#F26419] text-white flex items-center justify-center disabled:opacity-40 hover:bg-[#D94F2B]">
                <Send className="w-5 h-5" />
              </button>
            </div>
          </div>
        </footer>
      ) : (
        <footer className="sticky bottom-0 bg-white border-t border-[#E2DFD8]">
          <div className="max-w-3xl mx-auto px-4 py-3 text-center text-xs text-[#75807A]">
            <Bell className="w-4 h-4 inline-block mr-1 -mt-0.5" />
            Ce canal est réservé aux annonces de la direction.
          </div>
        </footer>
      )}
    </div>
  );
}

const MessageBubble = ({ m, me, isAdmin, onPin, onDel, onReport }) => {
  const mine = me?.id === m.author_id;
  const roleTag = m.author_role === "admin" ? { label: "Direction", cls: "bg-[#F26419]/10 text-[#D94F2B]" }
    : m.author_role === "agent" ? { label: "Agent", cls: "bg-[#2563EB]/10 text-[#2563EB]" }
    : m.author_role === "driver" ? { label: "Chauffeur", cls: "bg-[#059669]/10 text-[#047857]" }
    : null;
  const date = m.created_at ? new Date(m.created_at).toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`forum-msg-${m.id}`}>
      <div className={`max-w-[85%] rounded-2xl p-3 ${mine ? "bg-[#F26419] text-white" : "bg-white border border-[#E2DFD8] text-[#1A1A1A]"} ${m.pinned ? "ring-2 ring-[#F26419]/40" : ""}`}>
        <div className={`flex items-center gap-2 text-[11px] mb-1 ${mine ? "text-white/80" : "text-[#75807A]"}`}>
          <span className="font-bold">{m.author_name}</span>
          {roleTag && <span className={`px-1.5 py-0.5 rounded ${mine ? "bg-white/20 text-white" : roleTag.cls}`}>{roleTag.label}</span>}
          <span className="opacity-70">· {date}</span>
          {m.pinned && <Pin className="w-3 h-3" />}
        </div>
        {m.text && <div className="whitespace-pre-wrap break-words text-sm">{m.text}</div>}
        {m.image_url && <img src={m.image_url} alt="Pièce jointe" className="mt-2 max-h-72 rounded-xl" />}
        <div className={`mt-1.5 flex items-center gap-2 text-[11px] ${mine ? "text-white/80" : "text-[#75807A]"}`}>
          {isAdmin && (
            <button data-testid={`forum-pin-${m.id}`} onClick={() => onPin(m.id)} className="hover:underline flex items-center gap-1"><Pin className="w-3 h-3" /> {m.pinned ? "Désépingler" : "Épingler"}</button>
          )}
          {(isAdmin || mine) && (
            <button data-testid={`forum-del-${m.id}`} onClick={() => onDel(m.id)} className="hover:underline flex items-center gap-1"><Trash2 className="w-3 h-3" /> Supprimer</button>
          )}
          {!mine && (
            <button data-testid={`forum-report-${m.id}`} onClick={() => onReport(m.id)} className="hover:underline flex items-center gap-1 ml-auto"><Flag className="w-3 h-3" /> Signaler</button>
          )}
        </div>
      </div>
    </div>
  );
};
